import { useMemo } from 'react'
import { Link } from 'react-router-dom'

import { ItemCard } from '@/components/ItemCard'
import { Alert, Button, EmptyState, PageSpinner } from '@/components/ui'
import { derive, groupByUrgency } from '@/domain/items'
import { formatMinor, totalsByCurrency } from '@/domain/money'
import { errorMessage } from '@/lib/pb'
import { useItems } from '@/lib/queries'

export function Dashboard() {
  const { data: items, isPending, error } = useItems()

  const groups = useMemo(() => {
    if (!items) return []
    // Archived and cancelled records are history, not something to act on.
    return groupByUrgency(items.filter((i) => i.status === 'active').map((i) => derive(i)))
  }, [items])

  const totals = useMemo(() => (items ? totalsByCurrency(items) : []), [items])

  if (isPending) return <PageSpinner />
  if (error) return <Alert>{errorMessage(error)}</Alert>

  if (!items || items.length === 0) {
    return (
      <EmptyState
        title="Nothing tracked yet"
        body="Try your passport, Netflix, or car insurance. Add the expiry date and we’ll email you before you need to act."
        action={
          <Link to="/items/new">
            <Button>Add your first reminder</Button>
          </Link>
        }
      />
    )
  }

  return (
    <div className="space-y-6">
      {/* Spend is useful context, not the primary job of the dashboard. */}
      {totals.length > 0 && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-ink-muted">
          {totals.map((total) => (
            <p key={total.currency}>
              <span className="font-medium text-ink">
                {formatMinor(total.monthlyMinor, total.currency)}
              </span>
              <span> /month</span>
              <span className="mx-1.5 text-line">·</span>
              <span>{formatMinor(total.annualMinor, total.currency)} a year</span>
              {totals.length > 1 && (
                <span className="ml-1 text-xs">({total.currency})</span>
              )}
            </p>
          ))}
        </div>
      )}

      {groups.length === 0 ? (
        <EmptyState
          title="Nothing active"
          body="Every record is archived. Reactivate one from All records, or add something new."
        />
      ) : (
        groups.map((group) => (
          <section key={group.key} aria-labelledby={`group-${group.key}`}>
            <div className="mb-2 flex items-baseline gap-2">
              <h2 id={`group-${group.key}`} className="text-sm font-semibold text-ink">
                {group.title}
              </h2>
              <span className="text-xs text-ink-muted">
                {group.items.length} · {group.hint}
              </span>
            </div>
            <div className="space-y-2">
              {group.items.map((derived) => (
                <ItemCard key={derived.item.id} derived={derived} />
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  )
}
